import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../hooks/useAuthCompat";
import { ChevronDown, Download, Loader2 } from "lucide-react";
import { useApi, publicApi, API } from "../hooks/useApi";
import { useCart } from "../context/CartContext";
import { AskAboutThis } from "../components/AskAboutThis";
import { getBundleSavings } from "../lib/bundleSavings";

export default function BookDetail() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { isSignedIn } = useUser();
  const { addItem } = useCart();
  const [insideOpen, setInsideOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const { data: book, isLoading } = useQuery({ queryKey: ["book", id], queryFn: async () => (await publicApi.get(`/books/${id}`)).data });
  const { data: catalog } = useQuery({ queryKey: ["books"], queryFn: async () => (await publicApi.get("/books")).data });
  const { data: library } = useQuery({ queryKey: ["library-home"], queryFn: async () => (await api.get("/library")).data, enabled: !!isSignedIn });

  const owned = !!library?.books?.find((b) => b.id === id);
  const savings = getBundleSavings(book, catalog);
  const downloadableComponents = (book?.bundle_components || []).filter((c) => c.book_id && c.pdf_available);
  const standaloneLocked = !!book && !book.is_bundle && !book.reader_ready && (!book.chapters || book.chapters.length === 0);

  const downloadPdf = async () => {
    setDownloadError("");
    setDownloading(true);
    const win = window.open("", "_blank");
    try {
      const { data } = await api.post(`/books/${id}/download/request`);
      if (win) {
        win.location = `${API}/books/download/deliver/${data.token}`;
        setTimeout(() => win.close(), 1500);
      }
    } catch (e) {
      if (win) win.close();
      setDownloadError(e?.response?.data?.detail || "PDF download currently unavailable.");
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading || !book) return <div className="max-w-3xl mx-auto px-6 py-24 text-white/70">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="book-detail-page">
      <div className="flex flex-col md:flex-row gap-10">
        <img src={book.cover_image} alt={book.title} className="w-56 aspect-square object-cover shrink-0 self-start" />
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-textSecondary mb-2">{book.category}</p>
          <h1 className="font-display text-display-sm md:text-display-md mb-2">{book.title}</h1>
          {book.subtitle && <p className="text-white/75 mb-6">{book.subtitle}</p>}
          {book.description_html ? (
            <div data-testid="book-description-html" className="text-white/70 max-w-xl prose-sm [&_h2]:text-white [&_h2]:text-lg [&_h2]:font-display [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-white [&_h3]:text-base [&_h3]:mt-3 [&_h3]:mb-1.5 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1 [&_strong]:text-white" dangerouslySetInnerHTML={{ __html: book.description_html }} />
          ) : (
            <p className="text-white/70 max-w-xl">{book.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-4 mt-8">
            {owned ? (
              <>
                {(book.is_bundle || book.reader_ready) && (
                  <button data-testid="book-open-reader-button" onClick={() => navigate(`/reader/${book.id}`)} className="bg-white text-black px-6 py-3 text-sm uppercase tracking-wide hover:bg-white/80 transition-colors duration-200">
                    Open in Reader
                  </button>
                )}
                {book.pdf_available ? (
                  <button
                    data-testid="book-download-pdf-button"
                    onClick={downloadPdf}
                    disabled={downloading}
                    className="border border-white/40 px-6 py-3 text-sm uppercase tracking-wide hover:border-white transition-colors duration-200 flex items-center gap-2 disabled:opacity-60"
                  >
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Download PDF
                  </button>
                ) : downloadableComponents.length > 0 ? (
                  downloadableComponents.map((c) => (
                    <Link key={c.book_id} to={`/read/${c.book_id}`} data-testid={`book-download-component-link-${c.book_id}`} className="border border-white/40 px-6 py-3 text-sm uppercase tracking-wide hover:border-white transition-colors duration-200 flex items-center gap-2">
                      <Download className="w-4 h-4" /> Download {c.title}
                    </Link>
                  ))
                ) : !standaloneLocked ? (
                  <span data-testid="book-download-pdf-unavailable" className="text-xs uppercase tracking-wide text-white/50 border border-white/15 px-4 py-3 max-w-xs">
                    {book.pdf_customer_message || "PDF download currently unavailable."}
                  </span>
                ) : null}
              </>
            ) : book.sale_blocked ? (
              <span data-testid="book-sale-blocked-note" className="text-xs uppercase tracking-wide text-white/60 border border-white/20 px-4 py-3 max-w-md">
                Temporarily unavailable for purchase — this {book.is_bundle ? "bundle" : "title"} is being re-produced.
              </span>
            ) : (
              <>
                <button data-testid="book-quick-buy-button" onClick={() => navigate(`/checkout?buyNow=${book.id}`)} className="bg-white text-black px-6 py-3 text-sm uppercase tracking-wide hover:bg-white/80 transition-colors duration-200">
                  Quick Buy — ${book.price.toFixed(2)}
                </button>
                <button data-testid="book-add-to-cart-button" onClick={() => addItem(book.id, book.title)} className="border border-white/30 px-6 py-3 text-sm uppercase tracking-wide hover:border-white transition-colors duration-200">
                  Add to Cart
                </button>
                {savings != null && (
                  <span data-testid="bundle-savings-note" className="text-sm text-brandAccent">You Save ${savings.toFixed(0)} vs. buying separately</span>
                )}
              </>
            )}
          </div>
          {downloadError && <p data-testid="book-download-error" className="text-xs text-white/70 mt-3">{downloadError}</p>}
          <div className="mt-5 max-w-xl space-y-1.5" data-testid="book-delivery-disclosure">
            {standaloneLocked ? (
              <p data-testid="book-reader-status-note" className="text-[11px] text-white/60">{book.reader_customer_message}</p>
            ) : (
              <>
                {book.reader_customer_message && (
                  <p data-testid="book-reader-status-note" className="text-[11px] text-white/60">{book.reader_customer_message}</p>
                )}
                {!owned && (
                  <p data-testid="book-pdf-status-note" className="text-[11px] text-white/60">
                    {book.pdf_available ? "Downloadable PDF available once owned." : (book.pdf_customer_message || "PDF download currently unavailable.")}
                  </p>
                )}
              </>
            )}
          </div>
          <div className="mt-6">
            <AskAboutThis type="book" refId={book.id} title={book.title} />
          </div>
        </div>
      </div>

      {book.bundle_components?.length > 0 && (
        <div className="mt-10 border border-white/15" data-testid="bundle-contents-section">
          <button
            data-testid="bundle-contents-toggle"
            onClick={() => setInsideOpen((v) => !v)}
            aria-expanded={insideOpen}
            className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/5 transition-colors duration-200"
          >
            <span className="text-sm uppercase tracking-wide">What's Inside — {book.bundle_components.length} eBooks</span>
            <ChevronDown className={`w-4 h-4 text-white/70 transition-transform duration-200 ${insideOpen ? "rotate-180" : ""}`} />
          </button>
          {insideOpen && (
            <div data-testid="bundle-contents-list" className="grid grid-cols-1 sm:grid-cols-3 gap-6 px-5 pb-6 border-t border-white/10 pt-6">
              {book.bundle_components.map((c) => {
                const testId = `bundle-component-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
                const inner = (
                  <>
                    <div className="w-28 aspect-[2/3] bg-white/5 flex items-center justify-center overflow-hidden">
                      <img src={c.cover_image} alt={c.title} className="max-w-full max-h-full object-contain" />
                    </div>
                    <p className="text-xs text-white/75 mt-3">{c.title}</p>
                  </>
                );
                return c.book_id ? (
                  <Link key={c.title} to={`/read/${c.book_id}`} data-testid={testId} className="flex flex-col items-center text-center group">
                    <div className="group-hover:opacity-80 transition-opacity duration-200 flex flex-col items-center">{inner}</div>
                    <span className="text-[10px] uppercase tracking-wide text-brandAccent mt-1.5">View Title →</span>
                  </Link>
                ) : (
                  <div key={c.title} data-testid={testId} className="flex flex-col items-center text-center">
                    {inner}
                    <span className="text-[10px] uppercase tracking-wide text-white/50 mt-1.5">Included in this bundle</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {book.chapters?.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl mb-4">Chapters</h2>
          <ul className="divide-y divide-white/10 border-t border-b border-white/10">
            {book.chapters.map((c) => (
              <li key={c.id} className="py-3 flex justify-between items-center text-sm">
                <span>{c.title}</span>
                {!c.is_preview && !owned && <span className="text-xs text-white/60 uppercase">Locked</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
